import { CoreConfig } from '../CoreConfig';
import { AuthFetch, JwtUtil } from '../auth';

import '../util/array.prototype';
import {
  ActiveStatus, ActiveStatuses, NotFoundError, Organization, PagedList, Permission, Person, PersonPref,
  Role, User, UserModel, ValidationError
} from '../models';
import { Validator } from '../util';
import { Logger } from './Logger';

export type IdSearchType = 'ANY' | 'STUDENT_ID' | 'EMPLOYEE_ID' | 'PIDM';

export const IdSearchTypes = {
  ANY: 'ANY' as IdSearchType,
  STUDENT_ID: 'STUDENT_ID' as IdSearchType,
  EMPLOYEE_ID: 'EMPLOYEE_ID' as IdSearchType,
  PIDM: 'PIDM' as IdSearchType
};

export interface SearchOptions {
  roles?: (string | Role)[];
  groups?: Organization[];
  roleMap?: { [role: string]: Organization[] };
  statuses: ActiveStatus[];
  userTypes?: string[];
  minModifiedDate?: Date;
  maxModifiedDate?: Date;
  rowsPerPage?: number;
  minimal?: boolean;
}

const DEFAULT_SEARCH_OPTS = {
  statuses: [ActiveStatuses.ACTIVE, ActiveStatuses.SUNSETTING],
  userTypes: ['Person'],
  rowsPerPage: 25
} as SearchOptions;

export class PrmService {
  private _timer: any;
  private _userCache: { [identifier: string]: User } = {};
  private _permissionsCache: { [userId: number]: Permission[] | Promise<Permission[]> } = {};

  constructor(private config: CoreConfig, private authFetch: AuthFetch, private logger: Logger = () => { /* noop */ }) {
    this._timer = setInterval(
      () => {
        this._userCache = {};
        this._permissionsCache = {};
      },
      12 * 60 * 60 * 1000     // 12 hours to match JWT and CAS expiration dates
    );

    if (this._timer.unref) {
      this._timer.unref();
    }
  }

  destroy() { clearInterval(this._timer); }

  search = async (searchText: string | null, opts: SearchOptions = DEFAULT_SEARCH_OPTS): Promise<User[]> => {
    const {
      roles = [],
      groups = [],
      roleMap = {},
      statuses,
      userTypes = [],
      minModifiedDate,
      maxModifiedDate,
      rowsPerPage = DEFAULT_SEARCH_OPTS.rowsPerPage,
      minimal
    } = { ...DEFAULT_SEARCH_OPTS, ...opts };

    const roleParam = roles
      .map(role => `&roles=${typeof role === 'string' ? role : role.code}`)
      .join('');
    const roleMapParam = Object.keys(roleMap)
      .flatMap(role =>
        roleMap[role].map((group: Organization) => `&roleMap[${role}]=${group.code || group.id}`)
      )
      .join('');
    const statusParam = statuses.map(status => `&statuses=${status}`).join('');
    const groupsParam = groups
      .filter(group => group != null)
      .map(group => `&parentGroupIdsOrCodes=${group.code || group.id}`)
      .join('');
    const userTypesParam = userTypes.map(type => `&userTypes=${type}`).join('');
    const searchParam = searchText ? `&greedySearch=${encodeURIComponent(searchText)}` : '';
    let modifiedParam = minModifiedDate ? `&minModifiedDate=${minModifiedDate.toISOString()}` : '';
    modifiedParam = maxModifiedDate ?
      `${modifiedParam}&maxModifiedDate=${maxModifiedDate.toISOString()}` :
      modifiedParam;
    const minimalParam = minimal != null ? `&minimal=${minimal}` : '';
    const urlParams = [
      roleParam, roleMapParam, statusParam, groupsParam, searchParam, userTypesParam, modifiedParam, minimalParam
    ]
      .join('');
    const url = `${this.config.prmUrl}/api/user?rowsPerPage=${rowsPerPage}${urlParams}`;

    const pagedList = await this.authFetch(url) as PagedList<User>;

    return pagedList.records.map(user => UserModel.create(user) as User);
  };

  findUsers = async (identifiers: (string | number)[], idType?: IdSearchType, useCache = true): Promise<User[]> => {
    const fetchPromises = identifiers
      .map((id: string | number) => this.findUser(id, idType, useCache));

    return Promise.all(fetchPromises);
  };

  findUser = async (identifier: string | number, idType?: IdSearchType, useCache = true): Promise<User> => {
    if (!Validator.isIdentifier(identifier)) {
      throw new ValidationError(`Invalid identifier ${identifier}`);
    }

    const cacheKey = (idType || '') + identifier;

    if (this._userCache[cacheKey] && useCache) {
      return this._userCache[cacheKey];
    }

    const idSearchTypeParam = idType ? `?idSearchType=${idType}` : '';
    const url = `${this.config.prmUrl}/api/user/${identifier}${idSearchTypeParam}`;
    let json;

    try {
      json = await this.authFetch(url) as User;
    } catch (err) {
      if ((err as NotFoundError).statusCode === 404) {
        throw new NotFoundError(`Cannot find user for identifier: ${identifier}`);
      }

      throw err;
    }

    if (!json || !json.id) {
      throw new Error('Invalid user JSON');
    }

    const user = UserModel.create(json) as User;

    if (useCache) {
      this._userCache[cacheKey] = user;
    }

    return user;
  };

  async merge<T extends User | Person | Organization>(partialUser: T): Promise<T> {
    const identifier = partialUser.id ||
      (partialUser as Person).loginId ||
      (partialUser as Person).studentId ||
      (partialUser as Person).employeeId ||
      (partialUser as Organization).code;

    const fullUser = await this.findUser(identifier as string | number);

    return UserModel.create({ ...(partialUser as User), ...fullUser }) as T;
  }

  async mergeAll<T extends User>(partialUsers: T[]): Promise<T[]> {
    const mergedUserPromises = partialUsers.map(partialUser => this.merge(partialUser));

    return Promise.all(mergedUserPromises);
  }

  async findByPidms(pidms: number[]): Promise<PagedList<Person>> {
    if (!pidms || pidms.length === 0) {
      return { page: 1, total: 0, records: [] };
    }

    const pidmParams = '&pidms=' + pidms.join('&pidms=');
    const url = `${this.config.prmUrl}/api/person?miminal=true${pidmParams}`;

    this.logger('findByPidms() from %s', url);

    const pagedListJson = await this.authFetch(url) as PagedList<Person>;

    const pagedList = {
      page: pagedListJson.page,
      total: pagedListJson.total,
      records: pagedListJson.records.map(json => UserModel.create(json) as Person)
    };

    this.logger('findByPidms() returned %d records', pagedList.records.length);

    return pagedList;
  }

  async hasPermission(authToken: string, ...allowed: string[]): Promise<boolean> {
    const permissions = await this.findPermissions(authToken);

    return permissions.some(permission => allowed.includes(permission.code));
  }

  async findPermissions(authToken: string): Promise<Permission[]> {
    if (!authToken) {
      throw new ValidationError('Missing auth token');
    }

    const { user, ipAddress } = JwtUtil.parse(authToken);
    const userId = user.id as number;
    const cacheKey = `${userId}_${ipAddress || ''}`;

    if (cacheKey in this._permissionsCache) {
      return this._permissionsCache[cacheKey];
    }

    const appNameDashed = this.config.appName.replace(/[^a-zA-Z0-9]/g, '-');
    const url = `${this.config.prmUrl}/api/current-user/${appNameDashed}/permission`;

    this.logger('findPermissions(%s) from %s', UserModel.create(user), url);

    const permissionsPromise = this.authFetch(url, { authToken }) as Promise<Permission[]>;
    this._permissionsCache[cacheKey] = permissionsPromise;

    const permissions = await permissionsPromise || [];

    if (permissions) {
      this._permissionsCache[cacheKey] = permissions;
    }

    return permissions;
  }

  async findPref(authToken: string): Promise<PersonPref> {
    if (!authToken) {
      throw new Error('Missing auth token');
    }

    const url = this.config.prmUrl + '/api/current-user/pref';

    return this.authFetch(url, { authToken }) as Promise<PersonPref>;
  }

  clearCache(userOrIdentifier: User | string | number) {
    const identifier = userOrIdentifier && (userOrIdentifier as User).id ?
      (userOrIdentifier as User).id as number :
      userOrIdentifier as string | number;

    delete this._userCache[identifier];

    const permissionCacheKey = Object.keys(this._permissionsCache)
      .find(key => key.startsWith(identifier.toString()));

    if (permissionCacheKey) {
      delete this._permissionsCache[permissionCacheKey];
    }
  }
}



// WEBPACK FOOTER //
// ./src/gs-core/common/services/PrmService.ts